
import { QueryChip } from 'components/ContentPanels/components/QueryChip'
import { SuggestedStatusSearches } from 'components/ContentPanels/components/SuggestedStatusSearches'
import { StatusesSearchResults } from 'components/CuratedFolderView/Statuses'
import HorizontalList from 'components/HorizontalList'
import { StatusIdea } from 'interfaces'
import { Fragment, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useDispatch } from 'react-redux'
import { NavLink, useNavigate } from 'react-router'
import { Subject } from 'rxjs'
import { filter, map, mergeMap, tap } from 'rxjs/operators'
import { searchStatusIdeas } from 'services/content/statuses/actions'
import { checkFeatureAvailability } from 'services/product'
import { message } from 'services/snackbar'
import { FEATURE_CONTENT_ON_DEMAND } from 'shared/constants'
import { randomize } from 'shared/utils'
import { StoreThunkDispatch } from 'store/state'
import { resetScroll } from 'utils/dom'
import { noop } from 'utils/noop'
import { updateState } from '../state/actions'
import { SearchContext } from '../state/context'
import styles from './SearchRoot.pcss'

const PAGE_SIZE = 100

export function SearchStatusIdeasRoute() {
  const intl = useIntl()
  const dispatch = useDispatch<StoreThunkDispatch>()
  const navigate = useNavigate()
  const [{ query, postIdeasTags, ideasTag }, searchDispatch] = useContext(SearchContext)
  const [items, setItems] = useState<StatusIdea[]>([])
  const [loading, setLoading] = useState(false)
  const [hasNextPage, setHasNextPage] = useState(true)
  const [selectedTag, setSelectedTag] = useState(ideasTag || '')
  // NOTE: searching by tag does not have server-side pagination. This is a client-side pagination
  const [paging, setPaging] = useState<{ currentPage: number, clientSide: boolean, visibleItems: StatusIdea[] }>(
    { currentPage: 1, clientSide: false, visibleItems: [] }
  )
  const page = useRef(0)

  useEffect(() => {
    resetScroll()
  }, [])

  useEffect(() => {
    dispatch(checkFeatureAvailability(
      FEATURE_CONTENT_ON_DEMAND,
      false,
      () => navigate({ pathname: `/content/search/sources/streams?q=${query}` })
    ))
  }, [dispatch, navigate, query])

  useEffect(() => {
    searchDispatch(updateState({ activeFilter: 'ideas', activeCategory: 'content' }))
  }, [searchDispatch])

  const search$ = useRef<Subject<{ q: string, tag?: string }>>(undefined)

  useEffect(() => {
    search$.current = new Subject()
    search$.current.pipe(
      filter(data => data.tag !== undefined || data.q.length > 0),
      tap(() => {
        setLoading(true)
      }),
      mergeMap(({ q, tag }) => {
        return dispatch(searchStatusIdeas(q, page.current, tag))
          .pipe(
            map((response: any) => {
              return { items: response.data, isTag: !q && Boolean(tag), count: response.totalCount }
            })
          )
      })
    )
      .subscribe((response: { items: StatusIdea[], isTag: boolean, count: number }) => {
        const newItems = response.isTag ? randomize(response.items) : response.items
        setItems(current => [...current, ...newItems])
        setLoading(false)
        const withClientSidePaging = response.count === response.items.length
        setPaging({ clientSide: withClientSidePaging, currentPage: 1, visibleItems: newItems.slice(0, PAGE_SIZE) })
      }, () => {
        setLoading(false)
        dispatch(message(intl.formatMessage({ id: 'errors.generic' }), 'error'))
      })

    return () => search$.current?.unsubscribe()
  }, [dispatch, intl])

  useEffect(() => {
    page.current = 0
    setItems([])
    setPaging({ currentPage: 1, clientSide: false, visibleItems: [] })
    search$.current?.next({ q: query, tag: selectedTag })
  }, [query, selectedTag])

  useEffect(() => {
    if (!query && !selectedTag && postIdeasTags.length > 0) {
      setSelectedTag(postIdeasTags[0])
    }
  }, [query, postIdeasTags, selectedTag])

  const loadNextPage = useCallback(() => {
    if (paging.clientSide) {
      if (paging.visibleItems.length < items.length) {
        setPaging(current => {
          const next = current.currentPage + 1
          return {
            ...current,
            currentPage: next,
            visibleItems: items.slice(0, next * PAGE_SIZE)
          }
        })
      }
      return
    }
    if (hasNextPage) {
      page.current += 1
      search$.current?.next({ q: query, tag: selectedTag })
    }
  }, [paging.clientSide, paging.visibleItems.length, items, hasNextPage, query, selectedTag])

  const onTagClick = (tag: string) => {
    setSelectedTag(current => tag === current ? '' : tag)
  }

  const onCompose = useCallback(() => {
    return !dispatch(checkFeatureAvailability(FEATURE_CONTENT_ON_DEMAND))
  }, [dispatch])

  return (
    <Fragment>
      <header className={styles.header}>
        <h2 className={`text-ellipsis ${styles.title}`}>
          <FormattedMessage id="search.navigation.ideas" />
        </h2>
        <NavLink to={`/content/search?q=${query}`}>
          <FormattedMessage id="general.carousel.nav.back" />
        </NavLink>
      </header>
      {postIdeasTags.length > 0 && (
        <HorizontalList className={styles['tags-list']} listHeight={40}>
          {postIdeasTags.map(tag => (
            <QueryChip
              key={tag}
              label={tag}
              isTag
              className={styles['tag-chip']}
              active={selectedTag === tag}
              onClick={onTagClick}
            />
          ))}
        </HorizontalList>
      )}
      {postIdeasTags.length > 0 && (
        <div className={styles['quotes-container']}>
          <SuggestedStatusSearches
            hideSavedSearches
            hideCategories
            activeQuery={query}
            className={styles['text-chip-box']}
            onQueryClick={noop}
          />
          <StatusesSearchResults
            search={{
              active: true,
              loading,
              page: page.current,
              query,
              hasNextPage
            }}
            items={paging.clientSide ? paging.visibleItems : items}
            onLoadMore={loadNextPage}
            onCompose={onCompose}
          />
        </div>
      )}
    </Fragment>
  )
}

export default SearchStatusIdeasRoute
